import type { Ref } from 'vue'

import { resolveClient } from '@/shared/ports/resolveClient'
import { MAX_STAGES_PER_RAID, RaidStageKind as RaidStageKindValue } from '../../types'
import type { RaidId, RaidStage, RaidStageId, RaidStageKind } from '../../types'
import {
  RaidAllStageDiffsResetDocument,
  RaidStageAddDocument,
  RaidStageDiffsResetDocument,
  RaidStageRemoveDocument,
  RaidStageReorderDocument,
  RaidStagesDocument,
  RaidStageUpdateDocument,
} from '../../lib/handWrittenGraphql'
import type {
  RaidAllStageDiffsResetMutation,
  RaidStageAddMutation,
  RaidStageDiffsResetMutation,
  RaidStageRemoveMutation,
  RaidStageReorderMutation,
  RaidStageUpdateMutation,
} from '../../lib/handWrittenGraphql'

// Local alias — see `stores/board/actions.ts` for the rationale.
const apolloClient = resolveClient()

function snowflakeVar(s: string): string {
  return s
}

function parseRaidStageKind(s: string): RaidStageKind {
  if (s === 'opening') return RaidStageKindValue.Opening
  if (s === 'forest') return RaidStageKindValue.Forest
  if (s === 'mid_push') return RaidStageKindValue.MidPush
  if (s === 'late_defense') return RaidStageKindValue.LateDefense
  if (s === 'endgame') return RaidStageKindValue.Endgame
  return RaidStageKindValue.Custom
}

function stageKindDefaultName(kind: RaidStageKind): string {
  if (kind === RaidStageKindValue.Forest) return 'Forest'
  if (kind === RaidStageKindValue.MidPush) return 'Towers'
  if (kind === RaidStageKindValue.LateDefense) return 'Goose'
  if (kind === RaidStageKindValue.Endgame) return 'Tree'
  if (kind === RaidStageKindValue.Opening) return 'Opening'
  return 'Custom'
}

export interface StageActionsCtx {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly error: Ref<string | null>
  /** See `fetchRaidBoard`: live subscription state wins any older query. */
  readonly liveSnapshotGeneration: Ref<number>
  readonly raidStages: Ref<ReadonlyArray<RaidStage>>
  readonly activeStageId: Ref<RaidStageId | null>
  readonly stageSelectionPending: Ref<boolean>
  readonly activeRaidId: Ref<RaidId | null>
  readonly runReporting: <T>(
    mutation: unknown,
    variables: Record<string, unknown>,
    acknowledge?: (result: T) => boolean,
  ) => Promise<boolean>
}

export interface StageActionsApi {
  /** True only when this request applied an authoritative stage catalog. */
  readonly loadStagesForRaid: (raidId: RaidId, shouldApply?: () => boolean) => Promise<boolean>
  readonly selectStage: (stageId: RaidStageId | null) => void
  readonly addStage: (input: {
    readonly kind: RaidStageKind
    readonly name?: string | null
    readonly notes?: string | null
  }) => Promise<boolean>
  readonly updateStage: (
    stageId: RaidStageId,
    input: {
      readonly kind?: RaidStageKind | null
      readonly name?: string | null
      readonly notes?: string | null
    },
  ) => Promise<boolean>
  readonly removeStage: (stageId: RaidStageId, _label: string) => Promise<boolean>
  readonly reorderStages: (orderedStageIds: ReadonlyArray<RaidStageId>) => Promise<boolean>
  readonly resetStageChanges: () => Promise<boolean>
  readonly resetAllStageChanges: () => Promise<boolean>
}

export function useStageActions(ctx: StageActionsCtx): StageActionsApi {
  async function loadStagesForRaid(raidId: RaidId, shouldApply: () => boolean = () => true): Promise<boolean> {
    const snapshotGeneration = ctx.liveSnapshotGeneration.value
    if (ctx.activeDiscordGuildId.value === '0') {
      if (shouldApply() && snapshotGeneration === ctx.liveSnapshotGeneration.value) {
        ctx.raidStages.value = []
        return true
      }
      return false
    }
    try {
      const res = await apolloClient.query<{
        raidStagesForRaid: ReadonlyArray<{
          readonly type: string
          readonly guildId: number
          readonly raidId: number
          readonly payload: string | null
        }>
      }>({
        query: RaidStagesDocument,
        variables: {
          guildId: snowflakeVar(ctx.activeDiscordGuildId.value),
          raidId: Number(raidId),
        } as never,
        fetchPolicy: 'network-only',
      })
      const stageEvents = res.data?.raidStagesForRaid
      if (stageEvents === undefined) {
        if (shouldApply() && snapshotGeneration === ctx.liveSnapshotGeneration.value) {
          ctx.error.value = 'Server returned an empty stage catalog response. Please try again.'
        }
        return false
      }
      const map = new Map<number, RaidStage>()
      for (const e of stageEvents) {
        if (e.payload === null) continue
        try {
          const parsed = JSON.parse(e.payload) as {
            readonly id: number
            readonly kind: string
            readonly name: string
            readonly notes: string
            readonly orderIndex: number
          }
          if (typeof parsed.id !== 'number') continue
          map.set(parsed.id, {
            id: String(parsed.id),
            raidId: String(e.raidId),
            kind: parseRaidStageKind(parsed.kind),
            name: typeof parsed.name === 'string' ? parsed.name : '',
            notes: parsed.notes ?? '',
            orderIndex: typeof parsed.orderIndex === 'number' ? parsed.orderIndex : 0,
          })
        } catch {
          // Ignore malformed payloads.
        }
      }
      const list: RaidStage[] = []
      map.forEach((s) => list.push(s))
      list.sort((a, b) => a.orderIndex - b.orderIndex)
      if (shouldApply() && snapshotGeneration === ctx.liveSnapshotGeneration.value) {
        ctx.raidStages.value = list
        return true
      }
      return false
    } catch (e: unknown) {
      if (shouldApply() && snapshotGeneration === ctx.liveSnapshotGeneration.value) {
        ctx.error.value = e instanceof Error ? e.message : String(e)
      }
      return false
    }
  }

  function selectStage(stageId: RaidStageId | null): void {
    if (ctx.activeStageId.value === stageId) return
    ctx.activeStageId.value = stageId
    ctx.stageSelectionPending.value = ctx.activeRaidId.value !== null
  }

  async function addStage(input: {
    readonly kind: RaidStageKind
    readonly name?: string | null
    readonly notes?: string | null
  }): Promise<boolean> {
    if (ctx.activeRaidId.value === null) {
      ctx.error.value = 'Select a raid before adding a stage.'
      return false
    }
    if (ctx.raidStages.value.length >= MAX_STAGES_PER_RAID) {
      ctx.error.value = `A raid can carry at most ${String(MAX_STAGES_PER_RAID)} stages.`
      return false
    }
    ctx.stageSelectionPending.value = true
    const succeeded = await ctx.runReporting<RaidStageAddMutation>(RaidStageAddDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(ctx.activeRaidId.value),
      kind: input.kind,
      name: input.name?.trim() || stageKindDefaultName(input.kind),
      notes: input.notes,
    }, (result) => result.raidStageAdd !== null)
    if (!succeeded) ctx.stageSelectionPending.value = false
    return succeeded
  }

  async function updateStage(
    stageId: RaidStageId,
    input: {
      readonly kind?: RaidStageKind | null
      readonly name?: string | null
      readonly notes?: string | null
    },
  ): Promise<boolean> {
    const raidId = ctx.activeRaidId.value
    if (raidId === null) {
      ctx.error.value = 'Select a raid before renaming a stage.'
      return false
    }
    return ctx.runReporting<RaidStageUpdateMutation>(RaidStageUpdateDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(raidId),
      stageId: Number(stageId),
      kind: input.kind,
      name: input.name,
      notes: input.notes,
    }, (result) => result.raidStageUpdate !== null)
  }

  async function removeStage(stageId: RaidStageId, _label: string): Promise<boolean> {
    const raidId = ctx.activeRaidId.value
    const selectedStageId = ctx.activeStageId.value
    if (
      raidId === null
      || selectedStageId === null
      || selectedStageId !== stageId
      || !ctx.raidStages.value.some((stage) => stage.id === selectedStageId)
    ) {
      return false
    }
    ctx.stageSelectionPending.value = true
    const succeeded = await ctx.runReporting<RaidStageRemoveMutation>(RaidStageRemoveDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(raidId),
      stageId: Number(selectedStageId),
    }, (result) => result.raidStageRemove)
    if (!succeeded) ctx.stageSelectionPending.value = false
    return succeeded
  }

  async function reorderStages(orderedStageIds: ReadonlyArray<RaidStageId>): Promise<boolean> {
    if (ctx.activeRaidId.value === null) return false
    const incoming = orderedStageIds.map(Number)
    const existing = ctx.raidStages.value.map((s) => Number(s.id))
    if (
      existing.length === incoming.length &&
      existing.every((id, i) => id === (incoming[i] ?? -1))
    ) {
      return true
    }
    return ctx.runReporting<RaidStageReorderMutation>(RaidStageReorderDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(ctx.activeRaidId.value),
      orderedStageIds: incoming,
    }, (result) => result.raidStageReorder)
  }

  async function resetStageChanges(): Promise<boolean> {
    const raidId = ctx.activeRaidId.value
    const stageId = ctx.activeStageId.value
    if (raidId === null || stageId === null) return false
    return ctx.runReporting<RaidStageDiffsResetMutation>(RaidStageDiffsResetDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(raidId),
      stageId: Number(stageId),
    }, (result) => result.raidStageDiffsReset)
  }

  async function resetAllStageChanges(): Promise<boolean> {
    const raidId = ctx.activeRaidId.value
    if (raidId === null) return false
    return ctx.runReporting<RaidAllStageDiffsResetMutation>(RaidAllStageDiffsResetDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(raidId),
    }, (result) => result.raidAllStageDiffsReset)
  }

  return {
    loadStagesForRaid,
    selectStage,
    addStage,
    updateStage,
    removeStage,
    reorderStages,
    resetStageChanges,
    resetAllStageChanges,
  }
}
